import { Component, OnInit, ViewEncapsulation, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { finalize } from 'rxjs/operators';
import { BillingService } from 'app/services/billing/billing.service';
import { BillingAddress, BillingResponse, ClientData } from 'app/services/billing/billing.type';
import { AlertService } from 'app/components/ui/alert/alert.service';

@Component({
    selector: 'billing-data',
    templateUrl: './billing-data.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        CommonModule,
        MatIconModule,
        MatButtonModule
    ]
})
export class BillingDataComponent implements OnInit {
    clientData: ClientData | null = null;
    billingAddress: BillingAddress | null = null;
    isLoading: boolean = true;

    constructor(
        private _billingService: BillingService,
        private _changeDetectorRef: ChangeDetectorRef,
        private _alertService: AlertService
    ) {}

    ngOnInit(): void {
        this.loadBillingData();
    }

    loadBillingData(): void {
        this.isLoading = true;

        this._billingService.getSubscription()
            .pipe(
                finalize(() => {
                    this._changeDetectorRef.markForCheck();
                })
            )
            .subscribe({
                next: (response: BillingResponse) => {
                    this.clientData = response.data?.clientData ?? null;
                    this.billingAddress = response.data?.billingAddress ?? null;
                },
                error: () => {
                    this._alertService.error('Erro ao carregar dados de cobrança', { autoClose: true });
                    this.isLoading = false;
                },
                complete: () => {
                    this.isLoading = false;
                }
            });
    }

    /**
     * Formata o número do documento (CNPJ/CPF) com máscara
     */
    get formattedDocNumber(): string {
        const doc = this.clientData?.docNumber;
        if (!doc) {
            return '—';
        }

        const digits = doc.replace(/\D/g, '');

        // CNPJ: 14 dígitos
        if (digits.length === 14) {
            return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
        }

        // CPF: 11 dígitos
        if (digits.length === 11) {
            return digits.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
        }

        return doc;
    }

    /**
     * Cidade/Estado combinados com fallback '—' para campos vazios
     */
    get formattedCityState(): string {
        const city = this.billingAddress?.city;
        const state = this.billingAddress?.state;

        if (city && state) {
            return `${city} - ${state}`;
        }
        if (city) {
            return city;
        }
        if (state) {
            return state;
        }
        return '—';
    }

    /**
     * Helper de fallback para valores vazios/nulos
     */
    getDisplayValue(value: string | null | undefined): string {
        return value || '—';
    }
}
